import { create } from 'zustand';
import { api } from '@/lib/api';
import type { GridAnchor } from '@/lib/grid-anchor';

export interface ListingCardData {
  listing_id: string;
  slot: number;
  advertiser_id: string;
  username: string;
  /** The handle the card prints. A credited agency's own stands in for its roster. */
  handle: string;
  working_name: string;
  age: number | null;
  display_location: string | null;
  /** The suburb. Null where the account has named only a region. */
  city: string | null;
  /** The region it is grouped under, and its id: what a band on the grid names. */
  region: string | null;
  region_id: string | null;
  state: string | null;
  /**
   * Which band of a place page this card fell in, decided by the server. Null on the ungrouped
   * feed, which names no place. The words a band prints are chosen here, because only this side
   * knows which place the reader asked about.
   */
  band: 'locality' | 'region' | 'elsewhere' | 'nearby' | null;
  brand_name: string | null;
  brand_logo_key: string | null;
  /** The colour pack: the card is outlined in its colour. Null for none. */
  pack: { name: string; colour: string } | null;
  rate_cents: number | null;
  duration_minutes: number | null;
  online: boolean;
  status: 'now' | 'verified';
  bio_title: string | null;
  /** Who the advertiser sees (`men`, `women`, `disabled`), or null. */
  escort_for: string | null;
  incall: boolean;
  outcall: boolean;
  /** Ways to get in touch, as flags. All false while asleep. */
  call: boolean;
  sms: boolean;
  email: boolean;
  images: {
    card_sm: string | null;
    card: string | null;
    card_2x: string | null;
    profile: string | null;
  };
  /** The random gallery's card is one opted-in photograph; one listing can stand behind several. */
  photo_id?: string;
}

export interface SearchFilters {
  /**
   * The place the feed is built around. Not a filter: it decides the order and the bands, and
   * the set is the whole of that place's state plus whatever is inside the cross-border radius.
   */
  subject?: string;
  /**
   * What the subject means: `place` (the default) is a city page, the whole state banded;
   * `search` is a place searched for, its own advertisements then the places within the search
   * radius of it, and nothing further.
   */
  scope?: 'place' | 'search';
  q?: string;
  /**
   * The random gallery's shuffle. Set, it is the only question: the page asks `/gallery` for
   * every listing in this seed's order, and the other filters are not sent.
   */
  seed?: number;
  age_min?: number;
  age_max?: number;
}

interface SearchState {
  /**
   * The cards of the page being read, and only that page — or, in Scroll mode, of every page from
   * `firstPage` to `page`, appended in order.
   */
  listings: ListingCardData[];
  /** 1-based. Which numbered page `listings` is, or the last one appended to it. */
  page: number;
  /** The page `listings` starts at: `page` itself unless pages have been appended. */
  firstPage: number;
  /**
   * Where each appended page starts in `listings` (`at`) and which page it is: what Scroll mode
   * draws a divider at. Empty until a page is appended.
   */
  breaks: { at: number; page: number }[];
  /** The next page is being fetched to append. */
  appending: boolean;
  /** Appending failed: the run says so and offers it again, rather than stopping in silence. */
  appendFailed: boolean;
  /** How many pages the query has. 1 until the first answer lands. */
  pages: number;
  /** Every card the query matches, across all pages. */
  total: number;
  mediaBase: string;
  /** Cards per row on the random gallery (`gallery.per_row`), from its last answer. */
  perRow: number;
  filters: SearchFilters;
  loading: boolean;
  /** True once an answer has landed for the current filters and page. */
  loaded: boolean;
  /**
   * The last question failed: offline, refused, or a place that does not exist. The grid says so
   * and offers the same question again, rather than holding "Loading" on screen for ever.
   */
  failed: boolean;
  /**
   * The card to come back to, and the screen it was left from. It is a position in `listings`
   * rather than a bookmark, which is why it lives here beside them and not in sessionStorage: an
   * anchor that outlived its list would point into a feed re-sorted by boost and activity since,
   * and land the reader on somebody else.
   */
  anchor: GridAnchor | null;
  /** `page` defaults to the page being read, which is the page the card was tapped on. */
  setAnchor: (listing_id: string, key: string, page?: number, photo_id?: string) => void;
  clearAnchor: () => void;
  setFilters: (filters: SearchFilters, page?: number) => Promise<void>;
  /** Ask the current question again, after a failure. */
  retry: () => Promise<void>;
  /**
   * Scroll mode: fetch the page after `page` and append it. Returns the page now last, or null
   * when nothing was appended (already at the end, already fetching, or it failed).
   */
  loadMore: () => Promise<number | null>;
}

interface PageAnswer {
  listings: ListingCardData[];
  page: number;
  pages: number;
  total: number;
  page_size?: number;
  media_base: string;
  per_row?: number;
}

function toQuery(filters: SearchFilters, page: number): string {
  const params = new URLSearchParams();
  if (filters.seed) {
    params.set('seed', String(filters.seed));
    if (page > 1) params.set('page', String(page));
    return params.toString();
  }
  if (filters.subject) params.set('subject', filters.subject);
  if (filters.subject && filters.scope === 'search') params.set('scope', 'search');
  if (filters.q) params.set('q', filters.q);
  if (filters.age_min) params.set('age_min', String(filters.age_min));
  if (filters.age_max) params.set('age_max', String(filters.age_max));
  if (page > 1) params.set('page', String(page));
  return params.toString();
}

/**
 * Numbered pages. The page number lives in the address, so a page is a place a reader can come
 * back to, send, or reload; this store only holds the page the address names. A boost landing
 * between two page loads can move one card across a boundary, and that is accepted.
 */
export const useSearch = create<SearchState>((set, get) => ({
  listings: [],
  page: 1,
  firstPage: 1,
  breaks: [],
  appending: false,
  appendFailed: false,
  pages: 1,
  total: 0,
  mediaBase: '',
  perRow: 3,
  filters: {},
  loading: false,
  loaded: false,
  failed: false,
  anchor: null,

  setAnchor: (listing_id, key, page, photo_id) =>
    set({ anchor: { listing_id, key, page: page ?? get().page, photo_id } }),
  clearAnchor: () => set({ anchor: null }),

  setFilters: async (filters, page = 1) => {
    // Every browsing route sets its filters on mount, so leaving a profile and coming back asks
    // for the same page again. The same question is not asked twice: the page the reader left is
    // still in hand, and with it the card they are about to be returned to.
    const current = get();
    if (current.loaded && toQuery(filters, page) === toQuery(current.filters, current.page)) return;

    // The anchor is a position in this list, so it dies with it. A new page cannot be re-entered
    // at a card the old one held.
    set({
      filters,
      page,
      firstPage: page,
      listings: [],
      breaks: [],
      loaded: false,
      loading: true,
      failed: false,
      appending: false,
      appendFailed: false,
      anchor: null,
    });
    // Another page was asked for while this one was in flight. Its answer is not ours to show.
    const stale = () => {
      const now = get();
      return now.page !== page || toQuery(now.filters, now.page) !== toQuery(filters, page);
    };
    let data: PageAnswer;
    try {
      data = await api(`${filters.seed ? '/gallery' : '/search'}?${toQuery(filters, page)}`);
    } catch {
      if (!stale()) set({ loading: false, failed: true });
      return;
    }

    if (stale()) return;
    set({
      listings: data.listings,
      pages: data.pages,
      total: data.total,
      mediaBase: data.media_base,
      ...(data.per_row ? { perRow: data.per_row } : {}),
      loading: false,
      loaded: true,
    });
  },

  retry: async () => {
    const { filters, page } = get();
    // `loaded` is false after a failure, so the same question is asked rather than skipped.
    await get().setFilters(filters, page);
  },

  loadMore: async () => {
    const { filters, page, pages, loaded, appending } = get();
    if (!loaded || appending || page >= pages) return null;
    const next = page + 1;
    const question = toQuery(filters, page);
    set({ appending: true, appendFailed: false });
    let data: PageAnswer;
    try {
      data = await api(`${filters.seed ? '/gallery' : '/search'}?${toQuery(filters, next)}`);
    } catch {
      if (toQuery(get().filters, get().page) === question) set({ appending: false, appendFailed: true });
      return null;
    }
    // The question changed while this page was in flight: it belongs to a list nobody is reading.
    if (toQuery(get().filters, get().page) !== question) return null;
    const current = get().listings;
    const held = new Set(current.map((listing) => listing.listing_id));
    set({
      // A boost landing between two page loads can move a card across the boundary; in one run
      // that would draw it twice, so a card already held is not drawn again.
      listings: [...current, ...data.listings.filter((listing) => !held.has(listing.listing_id))],
      breaks: [...get().breaks, { at: current.length, page: next }],
      page: next,
      pages: data.pages,
      total: data.total,
      appending: false,
    });
    return next;
  },
}));
